import React, { useState } from "react";
import * as Mui from "@mui/material";
import * as XLSX from "xlsx";
import moment from "moment";
import { FileDownloadOutlined, TableViewOutlined } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { SimpleTable, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { PlainInput } from "pages/PayRoll/Shared/statutoryUi";
import { CODES, ESIC_COLUMNS, checksumOf, codeName, esicFileName, esicRows, esicSheetRows, formatDay, money, monthLabel, num } from "./esiReportsData";

// ESIC File (R3): one file per ESI code per month, built from the finalised
// payroll. Exactly 6 columns, Excel 97–2003 with every cell as text.
// Generate stays off while the code has a Blocking issue; regenerating
// needs a reason, makes a new version and marks the old one Superseded.

const FILE_COLORS = { Current: "#12B76A", Superseded: "#6B7280" };
const PREVIEW_ROWS = 6;
const linkSx = { color: designColors.tealDark, fontWeight: designWeights.semibold, cursor: "pointer", border: 0, p: 0, background: "none", font: "inherit" };

const downloadXls = (name, rows) => {
  const sheet = XLSX.utils.aoa_to_sheet([ESIC_COLUMNS, ...esicSheetRows(rows)]);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, "ESIC");
  XLSX.writeFile(book, name, { bookType: "biff8" });
};

const FileTable = ({ rows, limit }) => (
  <SimpleTable
    fit
    heads={[
      { label: "IP Number", width: 120 },
      { label: "IP Name", width: 160 },
      { label: "No. of Days", align: "right", width: 90 },
      { label: "Total Monthly Wages", align: "right", width: 140 },
      { label: "Reason Code", width: 100 },
      { label: "Last Working Day", width: 130 },
    ]}
  >
    {(limit ? rows.slice(0, limit) : rows).map((row) => (
      <Mui.TableRow key={row.id}>
        <Mui.TableCell sx={tableCellSx}>{row.ip || <Mui.Box component="span" sx={{ color: "#DC2626" }}>Missing</Mui.Box>}</Mui.TableCell>
        <Mui.TableCell sx={tableCellSx}>{row.name}</Mui.TableCell>
        <Mui.TableCell align="right" sx={tableCellSx}>{row.days}</Mui.TableCell>
        <Mui.TableCell align="right" sx={tableCellSx}>{row.wages}</Mui.TableCell>
        <Mui.TableCell sx={tableCellSx}>{row.days === 0 ? (row.reason ? row.reason.code : <Mui.Box component="span" sx={{ color: "#DC2626" }}>Not mapped</Mui.Box>) : ""}</Mui.TableCell>
        <Mui.TableCell sx={tableCellSx}>{row.lwd}</Mui.TableCell>
      </Mui.TableRow>
    ))}
    {limit && rows.length > limit ? (
      <Mui.TableRow>
        <Mui.TableCell sx={tableCellSx}>…</Mui.TableCell>
        <Mui.TableCell colSpan={5} sx={{ ...tableCellSx, color: designColors.slate600 }}>{num(rows.length - limit)} more rows</Mui.TableCell>
      </Mui.TableRow>
    ) : null}
  </SimpleTable>
);

export const EsiFileTab = ({ month, codeFilter, issues, files, onGenerate, goTo }) => {
  const [codeValue, setCodeValue] = useState(() => (codeFilter !== "all" && CODES.some((code) => code.code === codeFilter) ? codeFilter : CODES[0].code));
  const [view, setView] = useState(null);
  const [regenerate, setRegenerate] = useState({ open: false, reason: "", error: "" });
  const code = CODES.find((item) => item.code === codeValue);
  const rows = esicRows(month, code);
  const blockers = issues.filter((item) => item.severity === "Blocking" && item.code?.code === code.code);
  const current = files.find((file) => file.code === code.code && file.month === month && file.status === "Current");
  const fileName = esicFileName(code, month, current ? current.version + 1 : 1);
  const totalWages = rows.reduce((sum, row) => sum + row.wages, 0);
  const listed = [...files].sort((a, b) => moment(b.generated_at).diff(moment(a.generated_at)));
  const zeroDays = rows.filter((row) => row.days === 0).length;

  const generate = (reason) => {
    // Generation re-validates first (AC-R05).
    if (blockers.length) {
      showToast("error", `Fix ${blockers.length} blocking issue${blockers.length === 1 ? "" : "s"} first.`);
      return;
    }
    const version = onGenerate(code.code, rows, reason);
    showToast("success", `${esicFileName(code, month, version)} generated${version > 1 ? `; version ${version - 1} is superseded` : ""}.`);
  };

  const fileLabel = (file) => `${codeName(CODES.find((item) => item.code === file.code))} · ${monthLabel(file.month)} · v${file.version}`;
  // A generated file keeps the rows it was built with (sample files rebuild them).
  const rowsOf = (file) => file.rows || esicRows(file.month, CODES.find((item) => item.code === file.code));

  return (
    <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 3fr) minmax(0, 2fr)" }, gap: 2, alignItems: "start" }}>
      <Mui.Stack spacing={1.5}>
        <Mui.Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {CODES.map((item) => (
            <Mui.Chip
              key={item.code}
              label={`${codeName(item)} · ${item.code}`}
              onClick={() => setCodeValue(item.code)}
              variant={item.code === codeValue ? "filled" : "outlined"}
              sx={{
                fontWeight: designWeights.semibold,
                ...(item.code === codeValue ? { backgroundColor: designColors.tealDark, color: designColors.white, "&:hover": { backgroundColor: designColors.tealDark } } : {}),
              }}
            />
          ))}
        </Mui.Stack>
        {blockers.length ? (
          <RequestInfoBanner
            tone="danger"
            title="Blocked"
            body={`${blockers.map((item) => `${item.name} (${item.emp_id}): ${item.problem.toLowerCase()}`).join("; ")}. Fix it in ${blockers[0].fix_in === "Eligibility" ? "Statutory › ESI › Eligibility" : blockers[0].fix_in}, then generate. The preview shows the rows ready so far.`}
            action={<SecondaryButton onClick={() => goTo("validation")} sx={{ whiteSpace: "nowrap" }}>See validation</SecondaryButton>}
          />
        ) : current ? (
          <RequestInfoBanner title={`Generated — version ${current.version}`} body={`${formatDay(current.generated_at)} by ${current.generated_by}. Regenerate only if the payroll data changed.`} />
        ) : (
          <RequestInfoBanner title="Ready" body="No blocking issues for this code. Generate the file, then upload it on the ESIC portal." />
        )}

        <Mui.Box sx={detailCardSx}>
          <Mui.Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
            <Mui.Typography sx={designType.sectionHeading}>Preview — {esicFileName(code, month)}</Mui.Typography>
            <Mui.Typography sx={designType.captionText}>Excel 97–2003 · all columns text</Mui.Typography>
          </Mui.Stack>
          <FileTable rows={rows} limit={PREVIEW_ROWS} />
          <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: "center" }} sx={{ mt: 1.5 }}>
            <Mui.Typography sx={designType.helperText}>
              {num(rows.length)} rows · wages {money(totalWages)}
              {zeroDays ? ` · ${zeroDays} with 0 days` : ""}
            </Mui.Typography>
            <Mui.Stack direction="row" spacing={1.5} alignItems="center">
              <SecondaryButton startIcon={<TableViewOutlined />} onClick={() => setView({ name: esicFileName(code, month), rows })}>View all rows</SecondaryButton>
              {blockers.length ? (
                <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626" }}>Fix {blockers.length} blocking issue{blockers.length === 1 ? "" : "s"} to enable</Mui.Typography>
              ) : null}
              <PrimaryButton
                disabled={Boolean(blockers.length) || !rows.length}
                onClick={() => (current ? setRegenerate({ open: true, reason: "", error: "" }) : generate(""))}
              >
                {current ? "Regenerate File" : "Generate File"}
              </PrimaryButton>
            </Mui.Stack>
          </Mui.Stack>
        </Mui.Box>
      </Mui.Stack>

      <Mui.Stack spacing={1.5}>
        <Mui.Box sx={detailCardSx}>
          <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1.5 }}>Generated Files</Mui.Typography>
          <SimpleTable
            fit
            heads={[
              { label: "File", width: 170 },
              { label: "Rows", align: "right", width: 60 },
              { label: "Generated", width: 100 },
              { label: "Status", width: 100 },
              { label: "", width: 100 },
            ]}
          >
            {listed.map((file) => {
              const fileCode = CODES.find((item) => item.code === file.code);
              const fileRows = rowsOf(file);
              const name = esicFileName(fileCode, file.month, file.version);
              return (
                <Mui.TableRow key={file.id}>
                  <Mui.TableCell sx={tableCellSx}>
                    {fileLabel(file)}
                    {file.reason ? <Mui.Typography sx={designType.captionText}>Why: {file.reason}</Mui.Typography> : null}
                  </Mui.TableCell>
                  <Mui.TableCell align="right" sx={tableCellSx}>{num(fileRows.length)}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>{formatDay(file.generated_at)}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>
                    <RequestStatusChip status={file.status} colorCode={FILE_COLORS[file.status]} />
                  </Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>
                    <Mui.Stack direction="row" spacing={1} alignItems="center">
                      <Mui.Box component="button" type="button" sx={linkSx} onClick={() => setView({ name, rows: fileRows, file })}>View</Mui.Box>
                      <Mui.Tooltip title="Download" arrow>
                        <Mui.IconButton size="small" aria-label={`Download ${name}`} onClick={() => downloadXls(name, fileRows)}>
                          <FileDownloadOutlined fontSize="small" />
                        </Mui.IconButton>
                      </Mui.Tooltip>
                    </Mui.Stack>
                  </Mui.TableCell>
                </Mui.TableRow>
              );
            })}
          </SimpleTable>
        </Mui.Box>
        <Mui.Box sx={detailCardSx}>
          <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1 }}>Upload on the ESIC portal</Mui.Typography>
          {[
            "Download the file for the code",
            "Log in to the ESIC employer portal",
            "Monthly contribution → upload the file → submit",
            "Generate the challan and pay",
            "Record the challan in Reconciliation",
          ].map((step, index) => (
            <Mui.Typography key={step} sx={{ ...designType.helperText, py: 0.25 }}>
              {index + 1}. {step}
            </Mui.Typography>
          ))}
          <Mui.Typography sx={{ ...designType.captionText, mt: 1 }}>Cauver does not upload to the ESIC portal.</Mui.Typography>
        </Mui.Box>
      </Mui.Stack>

      <Mui.Dialog open={Boolean(view)} onClose={() => setView(null)} maxWidth="lg" fullWidth>
        <Mui.DialogTitle sx={{ ...designType.sectionHeading, fontSize: 16 }}>
          {view?.name}
          {view?.file ? (
            <Mui.Typography sx={designType.captionText}>
              Generated {moment(view.file.generated_at).format("DD MMM YYYY, HH:mm")} by {view.file.generated_by} · checksum {checksumOf(view.rows)}
            </Mui.Typography>
          ) : null}
        </Mui.DialogTitle>
        <Mui.DialogContent dividers>{view ? <FileTable rows={view.rows} /> : null}</Mui.DialogContent>
        <Mui.DialogActions sx={{ px: 3, py: 2 }}>
          <SecondaryButton onClick={() => setView(null)}>Close</SecondaryButton>
          <PrimaryButton startIcon={<FileDownloadOutlined />} disabled={!view?.rows.length} onClick={() => downloadXls(view.name, view.rows)}>
            Download
          </PrimaryButton>
        </Mui.DialogActions>
      </Mui.Dialog>

      <GlobalConfirmDialog
        open={regenerate.open}
        title="Regenerate ESIC file"
        onCancel={() => setRegenerate({ open: false, reason: "", error: "" })}
        onConfirm={() => {
          if (regenerate.reason.trim().length < 5) {
            setRegenerate((state) => ({ ...state, error: "Give the reason (it is kept with the file)" }));
            return;
          }
          generate(regenerate.reason.trim());
          setRegenerate({ open: false, reason: "", error: "" });
        }}
        confirmLabel="Regenerate"
        cancelLabel="Cancel"
        maxWidth="sm"
      >
        <Mui.Stack spacing={1.5}>
          <Mui.Typography sx={designType.tableBodyCell}>
            This makes {fileName}; version {current?.version} becomes Superseded. If the old version was already uploaded on the ESIC portal, say so in the reason.
          </Mui.Typography>
          <PlainInput
            value={regenerate.reason}
            onChange={(value) => setRegenerate((state) => ({ ...state, reason: value, error: "" }))}
            error={regenerate.error}
            placeholder="Why regenerate? e.g. IP number added for Manish T"
            inputProps={{ "aria-label": "Reason for regenerating" }}
          />
          {regenerate.error ? <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626" }}>{regenerate.error}</Mui.Typography> : null}
        </Mui.Stack>
      </GlobalConfirmDialog>
    </Mui.Box>
  );
};
